// SAM-199 follow-up — booking-option quantities for a service, now DERIVED from
// the Adults/Children total HowLongControl already collects (see
// deriveGuestMapping in serviceGuestMapping.ts) instead of asking the operator
// to enter guest counts a second time. Falls back to the original interactive
// counters only when the service's options don't fit a known shape.
//
// Never sends or computes a price: only whole-number quantities per option. The
// "reference" price shown per unit is exactly the number the server already
// returned on the service — nothing here multiplies it into a total. The real
// total is only known once the submit call returns it.

import type React from 'react';
import { Minus, Plus, Users } from 'lucide-react';
import type { BookingOption } from '../../../hooks/useServices';
import { clampQuantity, validateQuantities } from './serviceBooking';
import type { GuestMappingResult } from './serviceGuestMapping';
import { Notice } from '../../../components/ui/Notice';
import type { BookingRateGroup } from './bookingService';
import {
  effectiveGroupFor, hasGroupPrices, isPreviewOk, type PreviewLine, type PreviewMode, type PreviewResult,
} from './pricingPreview';
import { RateGroupPicker, linePriceText } from './ServicePricingControls';
import { labelOf } from './bookingService';
import { useT, type Lang, type TFunction } from '../../../i18n/admin';

// SAM-683: serviceGuestMapping / serviceBooking word their messages in English (and their tests
// pin that wording); they are translated here, at render time. Anything else shows as it came.
const optionName = (option: BookingOption, lang: Lang) =>
  labelOf({ en: option.label_en, es: option.label_es }, option.id, lang);

const translateMessage = (text: string, options: BookingOption[], t: TFunction, lang: Lang): string => {
  const byEnglishName = (name: string) => {
    const opt = options.find((o) => (o.label_en || o.id) === name);
    return opt ? optionName(opt, lang) : name;
  };
  let m = /^(.*): minimum is (\d+)\.$/.exec(text);
  if (m) return t('booking.qty.minimum', { option: byEnglishName(m[1]), n: m[2] });
  m = /^(.*): maximum is (\d+)\.$/.exec(text);
  if (m) return t('booking.qty.maximum', { option: byEnglishName(m[1]), n: m[2] });
  if (text === 'Select at least one guest.') return t('booking.qty.atLeastOne');
  if (text === 'This service has no booking options configured.') return t('booking.qty.noOptions');
  m = /^This service's guest tier_group \("(.*)"\) has (\d+) tiers — set quantities manually\.$/.exec(text);
  if (m) return t('booking.qty.tierGroupManual', { group: m[1], n: m[2] });
  return text;
};

type Props = {
  options: BookingOption[];
  mapping: GuestMappingResult;
  // SAM-624 — pricing v2. All optional so a caller without them renders exactly as before.
  mode?: PreviewMode;
  preview?: PreviewResult;
  rateGroups?: BookingRateGroup[];
  pickedGroups?: Record<string, string>;
  onRateGroupChange?: (optionId: string, groupId: string) => void;
  // Only used for the 'manual' fallback — the derived shapes never let the
  // operator edit quantities directly, since that would reintroduce the
  // second, parallel guest count this change removes.
  manualQuantities: Record<string, number>;
  onManualChange: (quantities: Record<string, number>) => void;
};

export const ServiceQuantitySelector = ({
  options, mapping, manualQuantities, onManualChange,
  mode = 'person', preview, rateGroups = [], pickedGroups = {}, onRateGroupChange,
}: Props) => {
  const i18n = useT();
  const { t, lang } = i18n;
  if (!options || options.length === 0) return null;

  const previewLine = (optionId: string) => (preview && isPreviewOk(preview) ? preview.lines.find((l) => l.optionId === optionId) : undefined);
  const groupPicker = (option: BookingOption | undefined, quantity: number) => {
    if (!option || quantity <= 0 || rateGroups.length === 0 || !hasGroupPrices(option) || !onRateGroupChange) return null;
    return (
      <RateGroupPicker
        option={option}
        groups={rateGroups}
        value={effectiveGroupFor(option, rateGroups, pickedGroups[option.id])}
        onChange={(gid) => onRateGroupChange(option.id, gid)}
      />
    );
  };

  if (mapping.kind === 'manual') {
    return (
      <div>
        <Notice variant="warn" className="mb-2">
          {mapping.reason ? translateMessage(mapping.reason, options, t, lang) : t('booking.qty.cannotDerive')}
        </Notice>
        <ManualQuantityCounters
          options={options}
          quantities={manualQuantities}
          onChange={onManualChange}
          mode={mode}
          previewLine={previewLine}
          groupPicker={groupPicker}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="cap" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Users size={12} />
        {t('booking.qty.fromAdultsChildren', { n: mapping.totalGuests })}
      </div>
      <div className="space-y-2">
        {mapping.breakdown.map((line) => {
          const option = options.find((o) => o.id === line.optionId);
          const pl = previewLine(line.optionId);
          // The server's (or preview's) included count wins over the tier-group hint when present.
          const free = mode === 'unit' ? (pl?.includedQuantity ?? 0) : line.freeQuantity;
          return (
            <div
              key={line.optionId}
              className="rounded-lg px-3 py-2"
              style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium" style={{ color: 'var(--ink-900)' }}>
                    {option ? optionName(option, lang) : line.label}
                  </div>
                  <div className="text-[11px]" style={{ color: 'var(--ink-500)' }}>
                    {option ? linePriceText(option, mode, pl, line.price, line.currency, i18n) : t('booking.price.perUnit', { price: i18n.fmt.money(line.price, line.currency) })}
                    {free > 0 && ` · ${t(mode === 'unit' ? 'booking.qty.includedInUnit' : 'booking.qty.includedFree', { n: free })}`}
                  </div>
                </div>
                <span className="text-sm" style={{ color: 'var(--ink-900)' }}>
                  {line.quantity}
                  {free > 0 && free < line.quantity && (
                    <span style={{ opacity: 0.7 }}> ({t(mode === 'unit' ? 'booking.qty.extra' : 'booking.qty.billable', { n: line.quantity - free })})</span>
                  )}
                </span>
              </div>
              {groupPicker(option, line.quantity)}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] mt-2" style={{ color: 'var(--ink-400)' }}>
        {t('booking.totalConfirmedByServer')}
      </p>
    </div>
  );
};

function ManualQuantityCounters({
  options,
  quantities,
  onChange,
  mode,
  previewLine,
  groupPicker,
}: {
  options: BookingOption[];
  quantities: Record<string, number>;
  onChange: (quantities: Record<string, number>) => void;
  mode: PreviewMode;
  previewLine: (optionId: string) => PreviewLine | undefined;
  groupPicker: (option: BookingOption | undefined, quantity: number) => React.ReactNode;
}) {
  const i18n = useT();
  const { t, lang } = i18n;
  const { problems } = validateQuantities(options, quantities);

  const setQuantity = (option: BookingOption, raw: number) => {
    onChange({ ...quantities, [option.id]: clampQuantity(option, raw) });
  };

  return (
    <div>
      <div className="space-y-2">
        {options.map((option) => {
          const qty = quantities[option.id] ?? 0;
          return (
            <div
              key={option.id}
              className="rounded-lg px-3 py-2"
              style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}
            >
             <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium" style={{ color: 'var(--ink-900)' }}>
                  {optionName(option, lang)}
                </div>
                {/* Reference only — read verbatim from the option, never multiplied client-side. */}
                <div className="text-[11px]" style={{ color: 'var(--ink-500)' }}>
                  {linePriceText(option, mode, previewLine(option.id), option.price, option.currency, i18n)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label={t('booking.qty.decrease', { item: optionName(option, lang) })}
                  onClick={() => setQuantity(option, qty - 1)}
                  className="rounded-full flex items-center justify-center"
                  style={{ width: 24, height: 24, border: '1px solid var(--line)', background: '#fff' }}
                >
                  <Minus size={12} />
                </button>
                <span className="text-sm w-6 text-center" style={{ color: 'var(--ink-900)' }}>{qty}</span>
                <button
                  type="button"
                  aria-label={t('booking.qty.increase', { item: optionName(option, lang) })}
                  onClick={() => setQuantity(option, qty + 1)}
                  className="rounded-full flex items-center justify-center"
                  style={{ width: 24, height: 24, border: '1px solid var(--line)', background: '#fff' }}
                >
                  <Plus size={12} />
                </button>
              </div>
             </div>
             {groupPicker(option, qty)}
            </div>
          );
        })}
      </div>

      {problems.length > 0 && (
        <ul className="text-[11px] mt-2 list-disc pl-4" style={{ color: 'var(--danger)' }}>
          {problems.map((problem) => <li key={problem}>{translateMessage(problem, options, t, lang)}</li>)}
        </ul>
      )}

      <p className="text-[11px] mt-2" style={{ color: 'var(--ink-400)' }}>
        {t('booking.totalConfirmedByServer')}
      </p>
    </div>
  );
}
